import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, timeclockAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
    Users, UserPlus, MoreVertical, Trash2, Shield, KeyRound,
    Loader2, Eye, EyeOff, User, BadgeCheck, Clock
} from 'lucide-react';

const ROLE_COLORS = {
    admin: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    manager: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    cashier: 'bg-green-500/10 text-green-600 border-green-500/20',
};

export default function Employees() {
    const { user } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [pinStatuses, setPinStatuses] = useState({});
    const [loading, setLoading] = useState(true);

    // Add employee dialog
    const [showAdd, setShowAdd] = useState(false);
    const [adding, setAdding] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [newEmp, setNewEmp] = useState({
        name: '', email: '', password: '', role: 'cashier', job_title: '', pin: '', confirmPin: ''
    });

    // Set PIN dialog
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [savingPin, setSavingPin] = useState(false);

    const isAdmin = user?.role === 'admin';
    const isManager = user?.role === 'manager';
    const canManage = isAdmin || isManager;

    const fetchEmployees = useCallback(async () => {
        setLoading(true);
        try {
            const [usersRes, pinsRes] = await Promise.all([
                usersAPI.getAll(),
                user?.store_id ? timeclockAPI.listPins() : Promise.resolve({ data: [] })
            ]);
            setEmployees(usersRes.data);
            const pinMap = {};
            (pinsRes.data || []).forEach(p => { pinMap[p.user_id] = p; });
            setPinStatuses(pinMap);
        } catch {
            toast.error('Could not load employees');
        } finally {
            setLoading(false);
        }
    }, [user?.store_id]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handleAddEmployee = async (e) => {
        e.preventDefault();
        if (newEmp.pin && newEmp.pin !== newEmp.confirmPin) {
            toast.error('PINs do not match'); return;
        }
        if (newEmp.pin && (newEmp.pin.length !== 4 || !/^\d{4}$/.test(newEmp.pin))) {
            toast.error('PIN must be exactly 4 digits'); return;
        }
        setAdding(true);
        try {
            await usersAPI.create({
                name: newEmp.name,
                email: newEmp.email,
                password: newEmp.password,
                role: newEmp.role,
                job_title: newEmp.job_title || null,
                pin: newEmp.pin || null,
                store_id: user?.store_id
            });
            toast.success(`${newEmp.name} added${newEmp.pin ? ' with PIN' : ''}`);
            setNewEmp({ name: '', email: '', password: '', role: 'cashier', job_title: '', pin: '', confirmPin: '' });
            setShowAdd(false);
            fetchEmployees();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add employee');
        } finally {
            setAdding(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await usersAPI.updateRole(userId, newRole);
            toast.success('Role updated');
            fetchEmployees();
        } catch {
            toast.error('Failed to update role');
        }
    };

    const handleDelete = async (emp) => {
        if (emp.id === user?.id) { toast.error('Cannot delete your own account'); return; }
        if (!window.confirm(`Delete ${emp.name}? Their PIN will also be removed.`)) return;
        try {
            await usersAPI.delete(emp.id);
            toast.success(`${emp.name} removed`);
            fetchEmployees();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete employee');
        }
    };

    const openPinDialog = (emp) => {
        setPinTarget(emp);
        setNewPin('');
        setConfirmPin('');
        setPinDialogOpen(true);
    };

    const handleSavePin = async () => {
        if (!newPin || newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
            toast.error('PIN must be exactly 4 digits'); return;
        }
        if (newPin !== confirmPin) { toast.error('PINs do not match'); return; }
        setSavingPin(true);
        try {
            await timeclockAPI.createPin({ user_id: pinTarget.id, pin: newPin });
            toast.success(`PIN set for ${pinTarget.name}`);
            setPinDialogOpen(false);
            fetchEmployees();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set PIN');
        } finally {
            setSavingPin(false);
        }
    };

    const storeEmployees = employees.filter(e => e.store_id === user?.store_id);

    return (
        <Layout title="Employees">
            <div className="max-w-4xl space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold">Team Members</h2>
                        <p className="text-sm text-muted-foreground">
                            {storeEmployees.length} employee{storeEmployees.length !== 1 ? 's' : ''} in your store
                        </p>
                    </div>
                    {canManage && (
                        <Button onClick={() => setShowAdd(true)}>
                            <UserPlus className="h-4 w-4 mr-2" /> Add Employee
                        </Button>
                    )}
                </div>

                {/* Employee Table */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" /> Staff Roster
                        </CardTitle>
                        <CardDescription>Manage roles, PINs, and access for your team</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : storeEmployees.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <User className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                <p className="font-medium">No employees yet</p>
                                <p className="text-sm">Add your first team member to get started</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Job Title</TableHead>
                                        <TableHead>PIN</TableHead>
                                        {canManage && <TableHead className="w-10" />}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {storeEmployees.map(emp => (
                                        <TableRow key={emp.id}>
                                            <TableCell className="font-medium">
                                                {emp.name}
                                                {emp.id === user?.id && (
                                                    <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">{emp.email}</TableCell>
                                            <TableCell>
                                                {canManage && emp.id !== user?.id ? (
                                                    <Select
                                                        value={emp.role}
                                                        onValueChange={(val) => handleRoleChange(emp.id, val)}
                                                    >
                                                        <SelectTrigger className="h-7 w-28 text-xs">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="admin">Admin</SelectItem>
                                                            <SelectItem value="manager">Manager</SelectItem>
                                                            <SelectItem value="cashier">Cashier</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <Badge variant="outline" className={`text-xs ${ROLE_COLORS[emp.role] || ''}`}>
                                                        {emp.role === 'admin' && <Shield className="h-3 w-3 mr-1" />}
                                                        {emp.role === 'manager' && <BadgeCheck className="h-3 w-3 mr-1" />}
                                                        {emp.role}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {emp.job_title || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {pinStatuses[emp.id] ? (
                                                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/20">
                                                        <Clock className="h-3 w-3 mr-1" /> Set
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                                        Not set
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            {canManage && (
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="h-7 w-7">
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => openPinDialog(emp)}>
                                                                <KeyRound className="h-4 w-4 mr-2" />
                                                                {pinStatuses[emp.id] ? 'Reset PIN' : 'Set PIN'}
                                                            </DropdownMenuItem>
                                                            {emp.id !== user?.id && isAdmin && (
                                                                <>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem
                                                                        className="text-destructive"
                                                                        onClick={() => handleDelete(emp)}
                                                                    >
                                                                        <Trash2 className="h-4 w-4 mr-2" /> Remove
                                                                    </DropdownMenuItem>
                                                                </>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Add Employee Dialog */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Employee</DialogTitle>
                        <DialogDescription>Create a new team member account for your store</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddEmployee} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Full Name *</Label>
                                <Input
                                    placeholder="Jane Smith"
                                    value={newEmp.name}
                                    onChange={e => setNewEmp(p => ({ ...p, name: e.target.value }))}
                                    required
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Job Title</Label>
                                <Input
                                    placeholder="Cashier"
                                    value={newEmp.job_title}
                                    onChange={e => setNewEmp(p => ({ ...p, job_title: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                placeholder="jane@example.com"
                                value={newEmp.email}
                                onChange={e => setNewEmp(p => ({ ...p, email: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Password *</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Min 8 characters"
                                    value={newEmp.password}
                                    onChange={e => setNewEmp(p => ({ ...p, password: e.target.value }))}
                                    required
                                    minLength={8}
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    onClick={() => setShowPassword(v => !v)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Role</Label>
                            <Select
                                value={newEmp.role}
                                onValueChange={v => setNewEmp(p => ({ ...p, role: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cashier">Cashier</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    {isAdmin && <SelectItem value="admin">Admin</SelectItem>}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Time Clock PIN <span className="text-muted-foreground">(optional)</span></Label>
                                <Input
                                    placeholder="4 digits"
                                    maxLength={4}
                                    value={newEmp.pin}
                                    onChange={e => setNewEmp(p => ({ ...p, pin: e.target.value.replace(/\D/g, '') }))}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Confirm PIN</Label>
                                <Input
                                    placeholder="4 digits"
                                    maxLength={4}
                                    value={newEmp.confirmPin}
                                    onChange={e => setNewEmp(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, '') }))}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                            <Button type="submit" disabled={adding}>
                                {adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Add Employee
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Set PIN Dialog */}
            <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>
                            {pinStatuses[pinTarget?.id] ? 'Reset PIN' : 'Set PIN'} — {pinTarget?.name}
                        </DialogTitle>
                        <DialogDescription>4-digit PIN used for time clock check-in/out</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label>New PIN</Label>
                            <Input
                                placeholder="4 digits"
                                maxLength={4}
                                value={newPin}
                                onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Confirm PIN</Label>
                            <Input
                                placeholder="4 digits"
                                maxLength={4}
                                value={confirmPin}
                                onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPinDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSavePin} disabled={savingPin}>
                            {savingPin && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Save PIN
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
